import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle, Clock, UserCircle, Wrench, CurrencyDollar, Ticket } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);

export function PendingApprovalsPanel() {
  const { token } = useAuth();
  const [data, setData] = useState({ services: [], commissions: [] });
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectType, setRejectType] = useState("");
  const [approveId, setApproveId] = useState(null);
  const [approveType, setApproveType] = useState("");
  const [approveForm, setApproveForm] = useState({ amount: "", commission_percent: "10" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/pending-approvals`, { headers });
      setData(d);
    } catch (e) { if (process.env.NODE_ENV === "development") console.error(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, type, existingAmount) => {
    // If it's a service with no amount, show form to set amount
    if (type === "service" && (!existingAmount || existingAmount === 0)) {
      setApproveId(id);
      setApproveType(type);
      setApproveForm({ amount: "", commission_percent: "10" });
      return;
    }
    // Direct approve (has amount already - e.g. commissions from seller)
    const endpoint = type === "service" ? `/admin/services/${id}/approve` : `/admin/sales-commissions/${id}/approve`;
    try {
      await axios.post(`${API}${endpoint}`, {}, { headers });
      toast.success("Aprobado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleApproveWithAmount = async () => {
    if (!approveForm.amount || parseFloat(approveForm.amount) <= 0) {
      toast.error("Monto requerido para aprobar");
      return;
    }
    const endpoint = approveType === "service" ? `/admin/services/${approveId}/approve` : `/admin/sales-commissions/${approveId}/approve`;
    try {
      await axios.post(`${API}${endpoint}`, {
        amount: parseFloat(approveForm.amount),
        commission_percent: parseFloat(approveForm.commission_percent || 0),
      }, { headers });
      toast.success("Aprobado con monto asignado");
      setApproveId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error("Motivo de rechazo requerido"); return; }
    const endpoint = rejectType === "service" ? `/admin/services/${rejectId}/reject` : `/admin/sales-commissions/${rejectId}/reject`;
    try {
      await axios.post(`${API}${endpoint}`, { reason: rejectReason }, { headers });
      toast.success("Rechazado");
      setRejectId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const total = data.services.length + data.commissions.length;
  if (total === 0) return null;

  return (
    <div className="bg-white rounded-xl border overflow-hidden mb-4" data-testid="pending-approvals">
      <div className="bg-amber-500 px-5 py-3 flex items-center justify-between">
        <h3 className="text-white font-bold flex items-center gap-2"><Clock size={18} weight="fill" /> Pendientes de Aprobación ({total})</h3>
      </div>
      <div className="p-4 space-y-3">
        {data.services.map(s => (
          <div key={s.id} className="p-3 border border-amber-200 bg-amber-50/50 rounded-lg space-y-2" data-testid={`pending-svc-${s.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0"><Wrench size={18} className="text-blue-600" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">{s.service_name}</span>
                  <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">SERVICIO</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                  <span className="flex items-center gap-1"><UserCircle size={10} /> {s.assigned_to_name}</span>
                  <span>{s.client_name}</span>
                  <span className="font-mono">{s.date}</span>
                  {s.ticket_number && <span className="flex items-center gap-0.5 text-blue-600 font-semibold"><Ticket size={10} /> {s.ticket_number}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {s.amount > 0 ? (
                  <>
                    <p className="text-sm font-bold text-slate-800">{fmt(s.amount)}</p>
                    <p className="text-xs font-semibold text-blue-600">Bono: {fmt(s.commission_amount)}</p>
                  </>
                ) : (
                  <span className="text-[10px] text-amber-600 italic font-medium">Sin monto asignado</span>
                )}
              </div>
              {rejectId !== s.id && approveId !== s.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" onClick={() => handleApprove(s.id, "service", s.amount)} data-testid={`approve-svc-${s.id}`}>
                    <CheckCircle size={14} className="mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8 text-xs" onClick={() => { setRejectId(s.id); setRejectType("service"); }} data-testid={`reject-svc-${s.id}`}>
                    <XCircle size={14} className="mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </div>
            {/* Approve with amount form */}
            {approveId === s.id && (
              <div className="flex items-end gap-2 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <div>
                  <Label className="text-[10px] text-emerald-700 font-semibold">Monto ($) *</Label>
                  <Input type="number" value={approveForm.amount} onChange={e => setApproveForm({ ...approveForm, amount: e.target.value })} className="w-32 h-8 text-xs mt-0.5" placeholder="0" data-testid={`approve-amount-${s.id}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-emerald-700 font-semibold">% Comisión</Label>
                  <Input type="number" value={approveForm.commission_percent} onChange={e => setApproveForm({ ...approveForm, commission_percent: e.target.value })} className="w-24 h-8 text-xs mt-0.5" data-testid={`approve-pct-${s.id}`} />
                </div>
                {approveForm.amount && approveForm.commission_percent && (
                  <p className="text-xs font-bold text-emerald-700 pb-1">Bono: {fmt(parseFloat(approveForm.amount || 0) * parseFloat(approveForm.commission_percent || 0) / 100)}</p>
                )}
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" onClick={handleApproveWithAmount} data-testid={`approve-confirm-${s.id}`}>Aprobar</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setApproveId(null)}>Cancelar</Button>
              </div>
            )}
            {/* Reject form */}
            {rejectId === s.id && (
              <div className="flex items-center gap-2">
                <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Motivo de rechazo..." className="w-60 h-8 text-xs" data-testid={`reject-reason-${s.id}`} />
                <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleReject} data-testid={`reject-confirm-${s.id}`}>Rechazar</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setRejectId(null)}>X</Button>
              </div>
            )}
          </div>
        ))}

        {data.commissions.map(c => (
          <div key={c.id} className="p-3 border border-amber-200 bg-amber-50/50 rounded-lg space-y-2" data-testid={`pending-comm-${c.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center shrink-0"><CurrencyDollar size={18} className="text-teal-600" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">{c.equipment_name}</span>
                  <span className="text-[9px] bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-bold">COMISION</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                  <span className="flex items-center gap-1"><UserCircle size={10} /> {c.seller_name}</span>
                  <span>{c.client_name}</span>
                  <span className="font-mono">{c.date}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-slate-800">{fmt(c.amount)}</p>
                <p className="text-xs font-semibold text-teal-600">Comisión: {fmt(c.commission_amount)}</p>
              </div>
              {rejectId !== c.id && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" onClick={() => handleApprove(c.id, "commission", c.amount)} data-testid={`approve-comm-${c.id}`}>
                    <CheckCircle size={14} className="mr-1" /> Aprobar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8 text-xs" onClick={() => { setRejectId(c.id); setRejectType("commission"); }} data-testid={`reject-comm-${c.id}`}>
                    <XCircle size={14} className="mr-1" /> Rechazar
                  </Button>
                </div>
              )}
            </div>
            {rejectId === c.id && (
              <div className="flex items-center gap-2">
                <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Motivo de rechazo..." className="w-60 h-8 text-xs" data-testid={`reject-reason-${c.id}`} />
                <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleReject} data-testid={`reject-confirm-${c.id}`}>Rechazar</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setRejectId(null)}>X</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
